import {
  Battlefield,
  BattlefieldDefDTO,
  CombatPosition,
  CombatStateDTO,
  EntityCombatStateDTO,
  EntityStatus,
  EntityType,
  GameStateDTO,
  isPlayerControlled,
  TargetType,
} from '@hexcrawl/shared-utils';
import { useGameStore } from '@/ui/stores/gameStore';
import { useCombatStore } from '@/ui/stores/combatStore';
import { useNpcStore } from '@/ui/stores/npcStore';
import { abilityService } from '@/ui/services/abilityService';
import type { HydratedRoster } from './constants';

export class CombatContext {
  combat: CombatStateDTO;
  gameState: GameStateDTO;
  battlefield: Battlefield;
  readonly roster: HydratedRoster;
  readonly battlefieldDef: BattlefieldDefDTO;

  constructor(
    combat: CombatStateDTO,
    gameState: GameStateDTO,
    battlefield: Battlefield,
    roster: HydratedRoster,
    battlefieldDef: BattlefieldDefDTO,
  ) {
    this.combat = combat;
    this.gameState = gameState;
    this.battlefield = battlefield;
    this.roster = roster;
    this.battlefieldDef = battlefieldDef;
  }

  updateFromGameStore(): void {
    const storeState = useGameStore.getState().gameState;
    if (!storeState?.activeCombat) return;
    this.combat = storeState.activeCombat;
    this.gameState = storeState;
    this.battlefield = new Battlefield(this.combat, this.battlefieldDef);
  }

  getCurrentTurnEntity(): EntityCombatStateDTO | undefined {
    const order = this.getStableTurnOrder();
    if (order.length === 0) return undefined;
    const entityId = order[this.combat.turnIndex % order.length];
    const entity = this.combat.entityStates.find(e => e.entityId === entityId);
    if (entity?.status === EntityStatus.DEAD) {
      throw new Error(
        `Current turn entity ${entityId} is dead (turnIndex=${this.combat.turnIndex}, round=${this.combat.round}). ` +
        `The server should have advanced past dead entities.`
      );
    }
    return entity;
  }

  getStableTurnOrder(): string[] {
    const groups = this.combat.groupOrder && this.combat.groupOrder.length > 0
      ? this.combat.groupOrder
      : Array.from(new Set(this.combat.entityStates.map(e => e.groupId)));
    const order: string[] = [];
    for (const groupId of groups) {
      for (const state of this.combat.entityStates) {
        if (state.groupId === groupId) order.push(state.entityId);
      }
    }
    return order;
  }

  canPlayerMove(): boolean {
    if (this.combat.status !== 'active') return false;
    if (this.combat.turnHasMoved) return false;
    const combatStore = useCombatStore.getState();
    if (combatStore.queueBusy) return false;
    if (combatStore.inputMode !== 'movement') return false;
    const currentEntity = this.getCurrentTurnEntity();
    return currentEntity != null && isPlayerControlled(currentEntity);
  }

  isValidAbilityTarget(target: EntityCombatStateDTO): boolean {
    const combatStore = useCombatStore.getState();
    const abilityId = combatStore.selectedAbilityId;
    if (!abilityId) return false;

    const ability = abilityService.getAbilityFromCache(abilityId);
    if (!ability) return false;

    const caster = this.getCurrentTurnEntity();
    if (!caster) return false;

    const targetType = ability.target.type;
    const sameGroup = caster.groupId === target.groupId;
    const isSelf = caster.entityId === target.entityId;

    const typeAllowed =
      ((targetType & TargetType.SELF) && isSelf) ||
      ((targetType & TargetType.ALLIES) && sameGroup) ||
      ((targetType & TargetType.ENEMIES) && !sameGroup);

    if (!typeAllowed) return false;

    if (this.battlefield.distanceBetween(caster.position, target.position) > ability.target.range) {
      return false;
    }

    if (ability.target.requiresLos !== false) {
      if (!this.battlefield.hasLineOfSight(caster.position, target.position, [caster.entityId, target.entityId], caster.groupId)) {
        return false;
      }
    }

    return true;
  }

  isValidMoveTarget(pos: CombatPosition): boolean {
    const entityState = this.getCurrentTurnEntity();
    if (!entityState) return false;
    const entity = this.roster[entityState.entityId];
    if (!entity) return false;
    return this.battlefield.isValidMoveTarget(entity, entityState.position, pos);
  }

  getValidMovePositions(): CombatPosition[] {
    const currentEntity = this.getCurrentTurnEntity();
    if (!currentEntity) return [];
    const entity = this.roster[currentEntity.entityId];
    if (!entity) return [];
    return this.battlefield.getValidMovePositions(entity, currentEntity.position);
  }

  getAbilityRangeTiles(): CombatPosition[] {
    const abilityId = useCombatStore.getState().selectedAbilityId;
    if (!abilityId) return [];

    const ability = abilityService.getAbilityFromCache(abilityId);
    if (!ability || ability.target.range <= 1) return [];

    const caster = this.getCurrentTurnEntity();
    if (!caster) return [];

    const requiresLos = ability.target.requiresLos !== false;
    const tiles: CombatPosition[] = [];
    for (let x = 0; x < this.battlefield.width; x++) {
      for (let y = 0; y < this.battlefield.height; y++) {
        if (x === caster.position.x && y === caster.position.y) continue;
        if (this.battlefield.distanceBetween(caster.position, { x, y }) > ability.target.range) continue;
        if (requiresLos && !this.battlefield.hasLineOfSight(caster.position, { x, y }, [caster.entityId], caster.groupId)) continue;
        tiles.push({ x, y });
      }
    }
    return tiles;
  }

  getEntityVitals(
    entityState: EntityCombatStateDTO,
    maxHP: number,
    maxMP: number
  ): { currentHP: number; currentMP: number } {
    const combatWithEnemyStates = this.combat as CombatStateDTO & { enemyStates?: Array<{ entityId: string; currentHP: number; currentMP: number }> };
    let currentHP = maxHP;
    let currentMP = maxMP;

    if (entityState.entityType === EntityType.PLAYER && this.gameState.character) {
      currentHP = this.gameState.character.currentHP ?? maxHP;
      currentMP = this.gameState.character.currentMP ?? maxMP;
    } else if (entityState.entityType === EntityType.NPC) {
      const npc = useNpcStore.getState().npcs[entityState.entityId];
      if (npc) {
        currentHP = npc.currentHP ?? maxHP;
        currentMP = npc.currentMP ?? maxMP;
      }
    } else if (combatWithEnemyStates.enemyStates) {
      const enemyState = combatWithEnemyStates.enemyStates.find(s => s.entityId === entityState.entityId);
      if (enemyState) {
        currentHP = enemyState.currentHP;
        currentMP = enemyState.currentMP;
      }
    }

    return { currentHP, currentMP };
  }
}
